// بخش «داده پایه و نگاشت کد بیمه‌گر» صفحه تنظیمات MED (SCR-STAFF-706؛ AP-MED-02؛ US-MED-092): وضعیت دوازده فهرست §۴.۴،
// همگام‌سازی دستی، نگاشت‌های باز و فهرست اقلام بدون نگاشت با پیشنهاد خودکار بر اساس کد ملی دارو (AC-MED-092.2).
// قلم بدون نگاشت معتبر طبق BR-MED-032 به نسخه غیربیمه‌ای می‌رود، پس این فهرست مستقیماً روی نرخ رد نسخه اثر دارد.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type MedCatalogSyncOutcomeView,
  type MedInsurerCatalogVersionView,
  type MedInsurerCodeMapView,
  type MedUnmappedItemResponseItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { DEFAULT_INSURER, nationalKeys } from './national';

/** بخش داده پایه و نگاشت کد در صفحه تنظیمات MED. */
export function InsurerCatalogSection() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const insurer = DEFAULT_INSURER;
  const canManage = access.can('med.insurer_catalog.manage');

  const status = useQuery({
    queryKey: nationalKeys.catalog(tenantId, insurer),
    enabled: Boolean(tenantId) && canManage,
    queryFn: async () =>
      (
        await unwrap(
          tenantApi.GET('/api/v1/medical-orders/settings/insurer-catalog/{insurer}', { params: { path: { insurer } } }),
        )
      ).datasets,
  });

  const maps = useQuery({
    queryKey: nationalKeys.codeMaps(tenantId, insurer),
    enabled: Boolean(tenantId) && canManage,
    queryFn: async () =>
      (
        await unwrap(
          tenantApi.GET('/api/v1/medical-orders/settings/insurer-code-maps/{insurer}', { params: { path: { insurer } } }),
        )
      ).items,
  });

  const unmapped = useQuery({
    queryKey: nationalKeys.unmapped(tenantId, insurer),
    enabled: Boolean(tenantId) && canManage,
    queryFn: async () =>
      (
        await unwrap(
          tenantApi.GET('/api/v1/medical-orders/settings/insurer-code-maps/{insurer}/unmapped', {
            params: { path: { insurer }, query: { limit: 100 } },
          }),
        )
      ).items,
  });

  // فهرست مقدار و زمان مصرف برای تعیین پیش‌فرض نگاشت دارو (§۴.۱ هر دو را برای دارو الزامی کرده است).
  const amounts = useQuery({
    queryKey: nationalKeys.catalogItems(tenantId, insurer, 'DrugAmounts', null),
    enabled: Boolean(tenantId) && canManage,
    queryFn: async () =>
      (
        await unwrap(
          tenantApi.GET('/api/v1/medical-orders/settings/insurer-catalog/{insurer}/items', {
            params: { path: { insurer }, query: { dataset: 'DrugAmounts' } },
          }),
        )
      ).items,
  });

  const instructions = useQuery({
    queryKey: nationalKeys.catalogItems(tenantId, insurer, 'DrugInstructions', null),
    enabled: Boolean(tenantId) && canManage,
    queryFn: async () =>
      (
        await unwrap(
          tenantApi.GET('/api/v1/medical-orders/settings/insurer-catalog/{insurer}/items', {
            params: { path: { insurer }, query: { dataset: 'DrugInstructions' } },
          }),
        )
      ).items,
  });

  const [syncResult, setSyncResult] = useState<MedCatalogSyncOutcomeView[] | null>(null);

  const sync = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/settings/insurer-catalog/{insurer}:sync', {
          params: { path: { insurer } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSuccess: (data) => setSyncResult(data.outcomes),
    onSettled: (_data, error) => {
      idempotency.settle(error);
      void queryClient.invalidateQueries({ queryKey: nationalKeys.catalog(tenantId, insurer) });
      void queryClient.invalidateQueries({ queryKey: nationalKeys.unmapped(tenantId, insurer) });
    },
  });

  const [amount, setAmount] = useState('');
  const [instruction, setInstruction] = useState('');

  const accept = useMutation({
    mutationFn: (row: MedUnmappedItemResponseItem) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/settings/insurer-code-maps', {
          body: {
            sourceCode: row.sourceCode,
            insurer,
            serviceType: row.suggestedServiceType ?? '01',
            serviceCode: row.suggestedServiceCode ?? '',
            wasSuggested: true,
            defaultDrugAmountId: amount === '' ? null : Number(amount),
            defaultDrugInstructionId: instruction === '' ? null : Number(instruction),
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error);
      void queryClient.invalidateQueries({ queryKey: nationalKeys.codeMaps(tenantId, insurer) });
      void queryClient.invalidateQueries({ queryKey: nationalKeys.unmapped(tenantId, insurer) });
    },
  });

  const datasetColumns: DataTableColumn<MedInsurerCatalogVersionView>[] = [
    { id: 'dataset', headerKey: 'med:insurer_catalog.dataset', cell: (row) => <span>{row.dataset}</span> },
    { id: 'serviceType', headerKey: 'med:insurer_catalog.service_type', cell: (row) => <span>{row.serviceType ?? '—'}</span> },
    {
      id: 'version',
      headerKey: 'med:insurer_catalog.version',
      cell: (row) => <span data-testid={`med-catalog-version-${row.dataset}-${row.serviceType ?? 'none'}`}>{faDigits(row.version)}</span>,
    },
    { id: 'items', headerKey: 'med:insurer_catalog.item_count', cell: (row) => <span>{faDigits(row.itemCount)}</span> },
    {
      id: 'deactivated',
      headerKey: 'med:insurer_catalog.deactivated_count',
      cell: (row) => <span data-testid={`med-catalog-inactive-${row.dataset}-${row.serviceType ?? 'none'}`}>{faDigits(row.deactivatedCount)}</span>,
    },
    {
      id: 'synced',
      headerKey: 'med:insurer_catalog.synced_at',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{toJalaliDateTime(row.syncedAt, { timeZone })}</span>
          {row.isManual ? <Chip size="small" label={t('med:insurer_catalog.manual')} /> : null}
        </Stack>
      ),
    },
  ];

  const mapColumns: DataTableColumn<MedInsurerCodeMapView>[] = [
    { id: 'source', headerKey: 'med:insurer_catalog.source_code', cell: (row) => <span>{row.sourceCode}</span> },
    {
      id: 'code',
      headerKey: 'med:insurer_catalog.insurer_code',
      cell: (row) => <span data-testid={`med-map-code-${row.sourceCode}`}>{`${row.serviceType} · ${row.serviceCode}`}</span>,
    },
    { id: 'parGroup', headerKey: 'med:insurer_catalog.par_group', cell: (row) => <span>{row.parGroupCode ?? '—'}</span> },
    { id: 'terminology', headerKey: 'med:insurer_catalog.terminology', cell: (row) => <span>{row.terminology ?? '—'}</span> },
    {
      id: 'dose',
      headerKey: 'med:national.dose_amount',
      cell: (row) => <span>{`${row.defaultDrugAmountId ?? '—'} / ${row.defaultDrugInstructionId ?? '—'}`}</span>,
    },
    { id: 'validFrom', headerKey: 'med:insurer_catalog.valid_from', cell: (row) => <span>{row.validFrom}</span> },
  ];

  const unmappedColumns: DataTableColumn<MedUnmappedItemResponseItem>[] = [
    { id: 'source', headerKey: 'med:insurer_catalog.source_code', cell: (row) => <span>{row.name ?? row.sourceCode}</span> },
    {
      id: 'suggestion',
      headerKey: 'med:insurer_catalog.suggestion',
      cell: (row) => (
        <span data-testid={`med-unmapped-suggestion-${row.sourceCode}`}>
          {row.suggestedServiceCode ? `${row.suggestedServiceType} · ${row.suggestedServiceCode}` : '—'}
        </span>
      ),
    },
    {
      id: 'basis',
      headerKey: 'med:insurer_catalog.suggestion_basis',
      cell: (row) => <span>{row.suggestionBasis ? t(`med:insurer_catalog.basis_${row.suggestionBasis}`) : '—'}</span>,
    },
    {
      id: 'accept',
      headerKey: 'med:insurer_catalog.accept_suggestion',
      cell: (row) =>
        canManage && row.suggestedServiceCode ? (
          <Button size="small" variant="outlined" onClick={() => accept.mutate(row)} data-testid={`med-unmapped-accept-${row.sourceCode}`}>
            {t('med:insurer_catalog.accept_suggestion')}
          </Button>
        ) : null,
    },
  ];

  if (!canManage) {
    return null;
  }

  return (
    <Stack spacing={3} data-testid="med-insurer-catalog">
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h6">{t('med:insurer_catalog.title')}</Typography>
        <Button variant="contained" size="small" disabled={sync.isPending} onClick={() => sync.mutate()} data-testid="med-catalog-sync">
          {sync.isPending ? t('med:insurer_catalog.syncing') : t('med:insurer_catalog.sync')}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {t('med:insurer_catalog.subtitle')}
      </Typography>

      {syncResult?.some((outcome) => outcome.skipped) ? (
        <Alert severity="warning" data-testid="med-catalog-skipped">
          {t('med:insurer_catalog.skipped')}:{' '}
          {syncResult
            .filter((outcome) => outcome.skipped)
            .map((outcome) => `${outcome.dataset} (${outcome.errorCode ?? '—'})`)
            .join(t('common:list_separator'))}
        </Alert>
      ) : null}

      <DataTable
        rows={status.data}
        columns={datasetColumns}
        getRowId={(row) => `${row.dataset}-${row.serviceType ?? 'none'}`}
        isLoading={status.isLoading}
        error={status.error}
        onRetry={() => void status.refetch()}
        captionKey="med:insurer_catalog.title"
      />

      <Typography variant="subtitle1">{t('med:insurer_catalog.maps')}</Typography>
      <DataTable
        rows={maps.data}
        columns={mapColumns}
        getRowId={(row) => row.id}
        isLoading={maps.isLoading}
        error={maps.error}
        onRetry={() => void maps.refetch()}
        emptyTitleKey="med:insurer_catalog.empty_maps"
        captionKey="med:insurer_catalog.maps"
      />

      <Typography variant="subtitle1">{t('med:insurer_catalog.unmapped')}</Typography>
      <Stack direction="row" spacing={2}>
        <TextField
          select
          size="small"
          label={t('med:national.dose_amount')}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          data-testid="med-map-amount"
          sx={{ minWidth: 180 }}
        >
          {(amounts.data ?? []).map((item) => (
            <MenuItem key={item.code} value={item.code}>
              {item.name ?? item.code}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('med:national.dose_instruction')}
          value={instruction}
          onChange={(event) => setInstruction(event.target.value)}
          data-testid="med-map-instruction"
          sx={{ minWidth: 180 }}
        >
          {(instructions.data ?? []).map((item) => (
            <MenuItem key={item.code} value={item.code}>
              {item.name ?? item.code}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <DataTable
        rows={unmapped.data}
        columns={unmappedColumns}
        getRowId={(row) => row.sourceCode}
        isLoading={unmapped.isLoading}
        error={unmapped.error}
        onRetry={() => void unmapped.refetch()}
        emptyTitleKey="med:insurer_catalog.empty_unmapped"
        captionKey="med:insurer_catalog.unmapped"
      />
    </Stack>
  );
}
